import { alpha, type Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import {
  borderRadius,
  getModernScrollbarThin,
  layoutGap,
} from '../../../../theme/theme';
import {
  getWorkspaceIconButtonSx,
  getWorkspaceFooterSurfaceSx,
  getWorkflowWorkspacePalette,
  getWorkspaceTableSurfaceSx,
} from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { mappingDensity } from '../../../../shared/ui/mapping';

export const getKeyLibraryDialogPaperSx = (theme: Theme): SystemStyleObject<Theme> => ({
  backgroundColor: theme.palette.background.default,
  backgroundImage: 'none',
  color: theme.palette.text.primary,
  '& .MuiAlert-root': {
    borderRadius: `${borderRadius.md}px`,
    fontSize: '0.8125rem',
  },
  '& .MuiChip-root': {
    height: 26,
    borderRadius: `${borderRadius.full}px`,
    fontSize: '0.75rem',
    fontWeight: 650,
  },
});

export const getKeyLibraryHeaderSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'auto minmax(0, 1fr)',
      lg: 'auto auto minmax(0, 1fr) auto',
    },
    gridTemplateAreas: {
      xs: '"back tabs" "actions actions"',
      lg: '"back title tabs actions"',
    },
    alignItems: 'center',
    columnGap: 1,
    rowGap: { xs: 0.75, lg: 0 },
    minHeight: 48,
    px: { xs: 1, md: 1.5 },
    py: 0.5,
    flexShrink: 0,
    borderBottom: `1px solid ${palette.paneBorder}`,
    backgroundColor: palette.sectionRaisedBackground,
    backdropFilter: 'blur(8px)',
    '& [data-key-library-title]': {
      gridArea: 'title',
      display: { xs: 'none', lg: 'block' },
      color: palette.headingText,
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.25,
      whiteSpace: 'nowrap',
    },
  };
};

export const getKeyLibraryIconButtonSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    ...getWorkspaceIconButtonSx(theme),
    flex: '0 0 auto',
    border: 'none',
    backgroundColor: 'transparent',
    '&:hover': {
      border: 'none',
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: palette.headingText,
    },
  };
};

export const getKeyLibraryContentSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    px: 0,
    py: 0,
    backgroundColor: palette.shellBackground,
    '& > .MuiStack-root': {
      flex: 1,
      minHeight: 0,
    },
  };
};

export const getKeyLibraryToolbarSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    minHeight: 48,
    px: { xs: 1, md: 1.5 },
    py: 0.75,
    borderBottom: `1px solid ${palette.paneDivider}`,
    backgroundColor: 'transparent',
    '& .MuiButton-root': {
      minHeight: mappingDensity.tableInputHeight,
    },
  };
};

export const getKeyLibraryTabsSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    gridArea: 'tabs',
    minWidth: 0,
    minHeight: 36,
    backgroundColor: 'transparent',
    '& .MuiTabs-flexContainer': {
      gap: layoutGap.flush,
    },
    '& .MuiTabs-indicator': {
      height: 2,
      backgroundColor: theme.palette.secondary.main,
    },
    '& .MuiTab-root': {
      minHeight: 36,
      height: 36,
      minWidth: 0,
      px: 1.25,
      py: 0,
      border: 'none',
      borderRadius: `${borderRadius.md}px`,
      backgroundColor: 'transparent',
      color: palette.mutedText,
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: 0,
      textTransform: 'none',
    },
    '& .Mui-selected': {
      color: `${theme.palette.secondary.main} !important`,
      backgroundColor: alpha(theme.palette.secondary.main, 0.08),
    },
  };
};

export const getKeyLibraryHeaderActionsSx = (theme: Theme): SystemStyleObject<Theme> => ({
  gridArea: 'actions',
  minWidth: 0,
  justifyContent: { xs: 'flex-start', lg: 'flex-end' },
  flexWrap: { xs: 'wrap', lg: 'nowrap' },
  '& .MuiButton-root': {
    minHeight: mappingDensity.tableInputHeight,
    whiteSpace: 'nowrap',
  },
  '& .MuiIconButton-root': {
    border: 'none',
    backgroundColor: 'transparent',
  },
});

export const getKeyLibraryEmptyStateSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    alignSelf: 'flex-start',
    width: 'auto',
    maxWidth: 720,
    minHeight: 38,
    display: 'flex',
    alignItems: 'center',
    px: 1.25,
    py: 0.5,
    border: 'none',
    borderRadius: `${borderRadius.md}px`,
    backgroundColor: palette.sectionBackground,
    color: palette.bodyText,
  };
};

export const getKeyLibraryBlankSlateSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    placeItems: 'center',
    px: 2,
    py: 3,
    border: 'none',
    backgroundColor: 'transparent',
    '& > .MuiStack-root': {
      width: 'min(100%, 520px)',
      textAlign: 'center',
    },
    '& [data-key-library-empty-icon]': {
      display: 'grid',
      placeItems: 'center',
      width: 36,
      height: 36,
      borderRadius: `${borderRadius.full}px`,
      backgroundColor: alpha(theme.palette.secondary.main, 0.12),
      color: theme.palette.secondary.main,
    },
    '& h3': {
      color: palette.headingText,
      fontSize: '1rem',
      fontWeight: 750,
    },
  };
};

export const getKeyLibraryFooterSx = (theme: Theme): SystemStyleObject<Theme> => {
  return {
    ...getWorkspaceFooterSurfaceSx(theme),
    minHeight: 48,
    px: { xs: 1, md: 1.5 },
    py: 0.75,
    gap: 0.75,
    flexShrink: 0,
    '& .MuiButton-root': {
      minHeight: mappingDensity.tableInputHeight,
      px: 1.25,
      fontSize: '0.75rem',
      lineHeight: 1.2,
    },
  };
};

export const getKeyLibraryTableSurfaceSx = (theme: Theme): SystemStyleObject<Theme> => {
  return {
    ...getWorkspaceTableSurfaceSx(theme),
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    mx: { xs: 0.75, md: 1 },
    mb: { xs: 0.75, md: 1 },
  };
};

export const getKeyLibraryTableContainerSx = (
  theme: Theme,
  maxHeight?: string
): SystemStyleObject<Theme> => ({
  flex: 1,
  minHeight: 0,
  maxHeight: maxHeight ?? 'none',
  ...getModernScrollbarThin(theme, 'divider'),
});

export const getKeyLibraryTableSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    tableLayout: 'fixed',
    width: '100%',
    '& .MuiTableCell-root': {
      px: 1.25,
      py: 0.625,
      borderBottom: `1px solid ${palette.paneDivider}`,
      color: palette.bodyText,
      fontSize: '0.8125rem',
      lineHeight: 1.45,
      verticalAlign: 'middle',
    },
    '& .MuiTableCell-head': {
      py: 0.75,
      color: palette.headingText,
      backgroundColor: palette.sectionRaisedBackground,
      fontSize: '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.015em',
      whiteSpace: 'nowrap',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.045),
    },
    '& .MuiTableRow-root.Mui-selected': {
      backgroundColor: palette.selectedBackground,
    },
  };
};

export const getKeyLibraryReadOnlyCellSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  minHeight: mappingDensity.tableInputHeight,
  border: 'none',
  borderRadius: `${borderRadius.none}px`,
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  fontSize: '0.8125rem',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const getKeyLibraryColumnHeaderSx = (): SystemStyleObject<Theme> => ({
  display: 'flex',
  minWidth: 0,
  alignItems: 'center',
  '& [data-header-label]': {
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: 1.3,
  },
});

export const getKeyLibraryPaginationSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    minHeight: 40,
    height: 40,
    maxHeight: 40,
    py: '0 !important',
    overflow: 'hidden',
    borderTop: `1px solid ${palette.sectionBorder}`,
    color: palette.bodyText,
    backgroundColor: palette.sectionBackground,
    '& .MuiTablePagination-toolbar': {
      minHeight: 40,
      height: 40,
      maxHeight: 40,
      px: 1,
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      fontSize: '0.75rem',
    },
    '& .MuiTablePagination-select': {
      py: 0.25,
      fontSize: '0.75rem',
    },
    '& .MuiIconButton-root': {
      width: 28,
      height: 28,
      p: 0,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
};

export const getKeyLibraryCompactFieldSx = (
  theme: Theme,
  minWidth?: number | string
): SystemStyleObject<Theme> => ({
  minWidth,
  width: minWidth === '100%' ? '100%' : undefined,
  '& .MuiOutlinedInput-root': {
    minHeight: mappingDensity.tableInputHeight,
    borderRadius: `${borderRadius.sm}px`,
    backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'light' ? 0.92 : 0.28),
  },
  '& .MuiOutlinedInput-input': {
    px: 1,
    py: 0.75,
    fontSize: '0.8125rem',
    lineHeight: 1.35,
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.8125rem',
  },
});

// Inline-edit fields must read as plain cell text so the local-entries grid
// keeps the same visual rhythm as the read-only rules table; the affordance
// appears on hover/focus instead of a permanent outlined box per cell.
export const getKeyLibraryInlineEditFieldSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    width: '100%',
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      minHeight: mappingDensity.tableInputHeight,
      borderRadius: `${borderRadius.sm}px`,
      backgroundColor: 'transparent',
      transition: 'background-color 120ms ease',
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.mode === 'light' ? 0.05 : 0.07
        ),
      },
      '&.Mui-focused': {
        backgroundColor: alpha(
          theme.palette.background.paper,
          theme.palette.mode === 'light' ? 0.92 : 0.32
        ),
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'transparent',
      transition: 'border-color 120ms ease',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: palette.paneDivider,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
    // Errors must stay visible without focus, or invalid rows hide in the text-like styling.
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
    '& .MuiOutlinedInput-input': {
      px: 0.75,
      py: 0.5,
      fontSize: '0.8125rem',
      lineHeight: 1.4,
      '&::placeholder': {
        color: theme.palette.text.disabled,
        opacity: 0.8,
      },
    },
  };
};

export const getKeyLibraryMatchPanelSx = (theme: Theme): SystemStyleObject<Theme> => {
  const palette = getWorkflowWorkspacePalette(theme);
  return {
    maxWidth: 920,
    m: { xs: 1, md: 1.5 },
    p: { xs: 1.25, md: 1.5 },
    border: `1px solid ${palette.paneBorder}`,
    borderRadius: `${borderRadius.md}px`,
    backgroundColor: palette.sectionBackground,
  };
};
